import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ServicesCartContainer } from '@components/organisms/ServicesCartContainer/ServicesCartContainer';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { useData } from '@hooks/useData';
import { useGetServicesCartTotals } from '@services/useGetServicesCartTotals';
import { useServicesCart } from '@hooks/useServicesCart';
import { SITE_URLS } from '@constants';
import { Card } from '@components/atoms/Card';
import { Typography } from '@components/atoms/Typography';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Space } from '@components/atoms/Space';
import { RadioButton, RadioButtonType } from '@components/atoms/RadioButton';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import { pt } from 'react-day-picker/locale';
import { formatDateForAPI, formatDateToLongLocalized } from '@utils/format';
import { useGetSchedule } from '@services/useGetSchedule';
import styles from './ServicesBookingScheduling.module.css';

export const ServicesBookingScheduling: FC = () => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { request: requestSchedule } = useGetSchedule();
    const { request: requestServicesCartTotals } = useGetServicesCartTotals();
    const { login, servicesCartTotals, storeSchedule } = useData();
    const { storeUuid, services, displayWorkerStep, day, timeSlot, scheduleType } = useServicesCart();
    const { t } = useTranslation();

    const translations = {
        title: t('services-booking-scheduling-component.title'),
        nextButton: t('services-booking-scheduling-component.next.button'),
        previousButton: t('services-booking-scheduling-component.previous.button'),
    };

    const CURRENT_STEP = displayWorkerStep ? 3 : 2;

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            const redirectTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/${SITE_URLS.login}?redirectTo=${redirectTo}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // request employees, when entering page
    useEffect(() => {
        if (storeUuid) {
            requestServicesCartTotals(
                { uuid: storeUuid || '' },
                {
                    services: services
                        ?.filter((service) => service.checked === true && service.quantity > 0)
                        .map((service) => {
                            return { uuid: service.uuid, quantity: service.quantity };
                        }),
                }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeUuid]);

    useEffect(() => {
        if (storeUuid && servicesCartTotals?.data?.duration_min !== undefined) {
            requestSchedule({ uuid: storeUuid || '' }, { duration: servicesCartTotals.data.duration_min });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeUuid, servicesCartTotals?.data?.duration_min]);

    return (
        <ServicesCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Anchor
                        href={scheduleType === 'preBook' ? SITE_URLS.servicesBookingConfirm : SITE_URLS.servicesBookingPayment}
                        mode={AnchorMode.primary}
                        disabled={
                            // no services checked
                            services?.find((item) => item.checked === true) === undefined ||
                            // no schedule set
                            day === null ||
                            // time slot not chosen, when there are available week days
                            ((timeSlot === null || timeSlot === '') &&
                                Object.keys(storeSchedule?.data?.weekly_schedule || []).length > 0)
                        }>
                        {translations.nextButton}
                    </Anchor>

                    <Anchor
                        href={displayWorkerStep ? SITE_URLS.servicesBookingEmployees : SITE_URLS.servicesCart}
                        mode={AnchorMode.primary}
                        outline={true}>
                        {translations.previousButton}
                    </Anchor>
                </div>
            }>
            <Card>
                <div className="d-flex flex-column p-5 gap-5">
                    <DayPickerSection />
                    <TimeSlotPickerSection />
                    <hr />
                    <ObservationsSection />
                </div>
            </Card>
        </ServicesCartContainer>
    );
};

const dayNameToIndex: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

const DayPickerSection: FC = () => {
    const { storeSchedule } = useData();
    const { day, setDay, setTimeSlot } = useServicesCart();
    const [availableWeekdays, setAvailableWeekdays] = useState<number[]>([]);
    const { i18n } = useTranslation();

    // map available week days of store to calendar
    useEffect(() => {
        if (storeSchedule?.data?.weekly_schedule) {
            const days = Object.keys(storeSchedule?.data?.weekly_schedule || []);

            if (days.length > 0) {
                const weekdayIndexes = days.map((day) => dayNameToIndex[day]);
                setAvailableWeekdays(weekdayIndexes);
            } else {
                // if there are no intervals, set all week days available
                setAvailableWeekdays([0, 1, 2, 3, 4, 5, 6]);
            }
        }
    }, [storeSchedule]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isAvailableDay = (date: Date) => {
        return date >= today && availableWeekdays.includes(date.getDay());
    };

    const formatters: DayPickerProps['formatters'] = {};

    if (i18n.language === 'pt') {
        formatters.formatWeekdayName = (day: Date, options?: any) => {
            const dayIndex = day.getDay();
            const customWeekdayLabels = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
            return customWeekdayLabels[dayIndex];
        };
        formatters.formatCaption = (date: Date, options?: any) => {
            const formatter = new Intl.DateTimeFormat('pt', {
                month: 'long',
                year: 'numeric',
            });

            const parts = formatter.formatToParts(date);
            const month = parts.find((p) => p.type === 'month')?.value ?? '';
            const year = parts.find((p) => p.type === 'year')?.value ?? '';

            // capitalize first letter of month
            const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);

            return `${capitalizedMonth}, ${year}`;
        };
    }

    return (
        <div className={styles.customDayPicker}>
            <DayPicker
                locale={pt}
                mode="single"
                selected={day ? new Date(day) : undefined}
                onSelect={(date: Date) => {
                    if (day !== formatDateForAPI(date)) {
                        setDay(formatDateForAPI(date));
                        setTimeSlot('');
                    }
                }}
                disabled={(date) => !isAvailableDay(date)}
                modifiers={{ available: isAvailableDay }}
                startMonth={today}
                required={true}
                className={styles.calendar}
                classNames={{
                    day: styles.day,
                    selected: styles.day_selected,
                    disabled: styles.day_disabled,
                    nav: styles.nav,
                    months: styles.months,
                    month_caption: styles.month_caption,
                    caption_label: styles.month,
                    today: styles.today,
                    weekdays: styles.weekdays,
                }}
                formatters={formatters}
            />
        </div>
    );
};

const TimeSlotPickerSection: FC = () => {
    const { storeSchedule } = useData();
    const { day, timeSlot, setTimeSlot } = useServicesCart();
    const { t, i18n } = useTranslation();

    const translations = {
        morning: t('services-booking-scheduling-component.time-slots.morning'),
        afternoon: t('services-booking-scheduling-component.time-slots.afternoon'),
    };

    const indexToDayName = useMemo(() => {
        return Object.entries(dayNameToIndex).reduce<Record<number, string>>((acc, [name, index]) => {
            acc[index] = name;
            return acc;
        }, {});
    }, []);

    const weekdayName = day ? indexToDayName[new Date(day).getDay()] : null;
    // @ts-ignore
    const timeSlotsPerInterval = weekdayName ? storeSchedule?.data?.time_slots?.[weekdayName] || [] : [];

    return (
        <div>
            {day && (
                <>
                    <Typography color="dark" fontSize="18" fontWeight="900" breakpointsStyles={{ md: { fontSize: '20' } }}>
                        {formatDateToLongLocalized(new Date(day), i18n.language)}
                    </Typography>
                    {timeSlotsPerInterval.length > 0 && (
                        <>
                            <hr />
                            <div className="d-flex flex-column gap-5">
                                {timeSlotsPerInterval.map((timeSlots: string[], index: number) => (
                                    <div>
                                        {timeSlotsPerInterval.length === 2 && (
                                            <Typography
                                                classes="mb-2"
                                                fontSize="16"
                                                fontWeight="900"
                                                breakpointsStyles={{ md: { fontSize: '18' } }}>
                                                {index === 0 ? translations.morning : translations.afternoon}
                                            </Typography>
                                        )}
                                        <div className="d-flex flex-wrap gap-4">
                                            {timeSlots.map((item, timeSlotIndex) => (
                                                <div key={timeSlotIndex}>
                                                    <RadioButton
                                                        type={RadioButtonType.boxed}
                                                        id="time-slot"
                                                        name="time-slot"
                                                        value={item}
                                                        label={item}
                                                        checked={timeSlot === item}
                                                        onChange={() => setTimeSlot(item)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

const ObservationsSection: FC = () => {
    const { observations, setObservations } = useServicesCart();
    const { t } = useTranslation();

    const translations = {
        title: t('services-booking-scheduling-component.observations.label'),
    };

    return (
        <div>
            <Typography fontSize="18" fontWeight="900" breakpointsStyles={{ md: { fontSize: '20' } }}>
                {translations.title}
            </Typography>

            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

            <Input
                type={InputTypes.textarea}
                variant={InputVariants.regular}
                value={observations || ''}
                onChange={(value) => setObservations(value)}
            />
        </div>
    );
};
